import React, { useState } from 'react';
import { useJobStore } from '../../stores/jobStore';
import { useTranslation } from 'react-i18next';
import { FreelancerProfile } from '../../types/profile';
import { JobUpsertParams } from '../../types/job';
import FullBlockingModal from '../common/FullBlockingModal';
import UpsertJobForm from './UpsertJobForm';
import { TFunction } from 'i18next';
import JobPreview from './JobPreview';
import { Button } from '../common/Button';

const REQUEST_QUOTE_FORM_STEP = 'request_quote_form_step';
const REQUEST_QUOTE_CONFIRMATION_STEP = 'request_quote_confirmation_step';
const REQUEST_QUOTE_SENT_STEP = 'request_quote_sent_step';

interface RequestQuoteFormStepProps {
  t: TFunction;
  targetedFreelancer: FreelancerProfile;
  error?: string | null;
  jobParams?: JobUpsertParams | null;
  onNextStep: (jobParams: JobUpsertParams) => Promise<void>;
  onClose: () => void;
}

const RequestQuoteFormStep = ({
  t,
  targetedFreelancer,
  error,
  jobParams,
  onNextStep,
  onClose,
}: RequestQuoteFormStepProps) => {
  return (
    <>
      <h2 className="text-2xl font-bold text-secondary mb-4">
        {t('request_quote_modal.title', {
          freelancer: targetedFreelancer.first_name,
          ns: 'jobs',
        })}
      </h2>
      <UpsertJobForm
        submitButtonLabel={t('common:form_actions.next', { ns: 'common' })}
        submittingButtonLabel={t('common:form_actions.next', { ns: 'common' })}
        secondaryButtonLabel={t('common:form_actions.cancel_close', { ns: 'common' })}
        freelancerServiceCategories={targetedFreelancer.services}
        error={error}
        jobParams={jobParams}
        onCreateJob={onNextStep}
        onSecondaryButtonClick={onClose}
      />
    </>
  );
};

interface RequestQuoteConfirmationStepProps {
  t: TFunction;
  jobParams: JobUpsertParams;
  targetedFreelancer: FreelancerProfile;
  isSubmitting: boolean;
  onSubmit: () => void;
  onEdit: () => void;
  onClose: () => void;
}

const RequestQuoteConfirmationStep = ({
  t,
  jobParams,
  targetedFreelancer,
  isSubmitting,
  onSubmit,
  onEdit,
  onClose,
}: RequestQuoteConfirmationStepProps) => {
  return (
    <>
      <h2 className="text-2xl font-bold text-secondary mb-4">
        {t('request_quote_modal.title', {
          freelancer: targetedFreelancer.first_name,
          ns: 'jobs',
        })}
      </h2>
      <JobPreview job={jobParams} />
      <div className="flex flex-col gap-4 mt-8 md:gap-8 md:mt-4 md:flex-row md:flex-row-reverse md:items-center">
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="w-full md:w-auto md:px-8"
        >
          {isSubmitting
            ? t('common:form_actions.sending', { ns: 'common' })
            : t('common:form_actions.send', { ns: 'common' })}
        </Button>

        <Button
          type="button"
          onClick={onEdit}
          className="w-full md:w-auto md:px-8"
          variant="transparent-with-border"
        >
          {t('common:form_actions.edit', { ns: 'common' })}
        </Button>

        <Button
          type="button"
          onClick={onClose}
          className="w-full md:w-auto md:px-8"
          variant="simple-text"
        >
          {t('common:form_actions.cancel_close', { ns: 'common' })}
        </Button>
      </div>
    </>
  );
};

interface RequestQuoteSentStepProps {
  t: TFunction;
  targetedFreelancer: FreelancerProfile;
  onClose: () => void;
}

const RequestQuoteSentStep = ({ t, onClose, targetedFreelancer }: RequestQuoteSentStepProps) => {
  return (
    <>
      <h2 className="text-2xl font-bold text-secondary mb-4">
        {t('request_quote_modal.sent_title', { ns: 'jobs' })}
      </h2>
      <p className="text-secondary text-base mb-8">
        {t('request_quote_modal.sent_message', {
          ns: 'jobs',
          freelancer_name: targetedFreelancer.first_name,
        })}
      </p>
      <div className="flex flex-col gap-4 md:gap-8 md:flex-row md:flex-row-reverse md:items-center">
        <Button type="button" onClick={onClose} className="w-full md:w-auto md:px-8">
          {t('request_quote_modal.close_and_return_to_profile', { ns: 'jobs' })}
        </Button>
        <Button
          type="button"
          href="/jobs"
          className="w-full md:w-auto md:px-8"
          variant="transparent-with-border"
        >
          {t('request_quote_modal.see_my_jobs', { ns: 'jobs' })}
        </Button>
      </div>
    </>
  );
};

interface RequestQuoteModalProps {
  onClose: () => void;
  targetedFreelancer: FreelancerProfile;
}

const RequestQuoteModal: React.FC<RequestQuoteModalProps> = ({ onClose, targetedFreelancer }) => {
  const { t } = useTranslation(['jobs', 'common']);
  const { sendQuoteRequest } = useJobStore();
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<string>(REQUEST_QUOTE_FORM_STEP);
  const [jobParams, setJobParams] = useState<JobUpsertParams | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateJobFormNextStep = async (jobParams: JobUpsertParams) => {
    setJobParams(jobParams);
    setCurrentStep(REQUEST_QUOTE_CONFIRMATION_STEP);
  };

  const handleSendQuote = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await sendQuoteRequest(targetedFreelancer.id, jobParams!);
      setCurrentStep(REQUEST_QUOTE_SENT_STEP);
    } catch (error) {
      setError(t('request_quote_modal.error', { ns: 'jobs' }));
      console.error(error);
      setCurrentStep(REQUEST_QUOTE_FORM_STEP);
    } finally {
      setIsSubmitting(false);
    }
  };

  let CurrentComponent = null;
  switch (currentStep) {
    case REQUEST_QUOTE_SENT_STEP:
      CurrentComponent = (
        <RequestQuoteSentStep t={t} onClose={onClose} targetedFreelancer={targetedFreelancer} />
      );
      break;
    case REQUEST_QUOTE_CONFIRMATION_STEP:
      CurrentComponent = (
        <RequestQuoteConfirmationStep
          t={t}
          jobParams={jobParams!}
          targetedFreelancer={targetedFreelancer}
          isSubmitting={isSubmitting}
          onSubmit={handleSendQuote}
          onEdit={() => setCurrentStep(REQUEST_QUOTE_FORM_STEP)}
          onClose={onClose}
        />
      );
      break;
    case REQUEST_QUOTE_FORM_STEP:
    default:
      CurrentComponent = (
        <RequestQuoteFormStep
          t={t}
          targetedFreelancer={targetedFreelancer}
          error={error}
          onNextStep={handleCreateJobFormNextStep}
          onClose={onClose}
          jobParams={jobParams}
        />
      );
  }

  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <button
        className="absolute -top-1 right-1 text-gray-400 hover:text-gray-600 text-2xl md:top-0 md:right-4"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>
      {CurrentComponent}
    </FullBlockingModal>
  );
};

export default RequestQuoteModal;
